import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, Sparkles } from "lucide-react";
import { AppDispatch, RootState } from "../../../../../store";
import { getDailyCommissionDetails } from "../../../../../Redux/referral/slice";
import Pagenation from "../../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DailyCommDetailsTable from "./DailyCommDetailsTable";
import { formatEarningsCurrency } from "../../earningsAdapters";
import "../DailyIncentives.css";

const DailyCommissoinDetails = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const dateParam = queryParams.get("date") || "";
  const codeParam = queryParams.get("code") || "";

  const { dailyCommissionDetails } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    let monthString = "";

    if (codeParam && /^\d{6}$/.test(codeParam)) {
      monthString = codeParam;
    } else if (dateParam) {
      if (/^\d{6}$/.test(dateParam)) {
        monthString = dateParam;
      } else {
        const dateObj = new Date(dateParam);
        if (!isNaN(dateObj.getTime())) {
          const year = dateObj.getFullYear();
          const month = String(dateObj.getMonth() + 1).padStart(2, "0");
          monthString = `${year}${month}`;
        }
      }
    }

    if (monthString) {
      dispatch(
        getDailyCommissionDetails({
          month: monthString,
          page,
          limit,
          status: "ALL",
          sortOrder: "DESC",
        }),
      );
    }
  }, [dispatch, codeParam, dateParam, page, limit]);

  const monthYearDisplay =
    dailyCommissionDetails.monthSummary?.monthYear || "Selected Month";
  const totalEarned =
    dailyCommissionDetails.monthSummary?.totalReceived ?? 0;

  return (
    <section className="daily-ref-page">
      <div className="daily-ref-shell">
        <header className="daily-ref-hero daily-ref-details-hero">
          <div className="daily-ref-hero-left">
            <button
              type="button"
              className="daily-ref-back"
              onClick={() => navigate("/DailyCommissons")}
              aria-label="Back to daily incentives"
            >
              <ArrowLeft size={19} />
            </button>
            <div className="daily-ref-hero-copy">
              <span className="daily-ref-eyebrow">DAILY INCENTIVES</span>
              <h1>Daily Referral Commission Details</h1>
              <p>
                For: {monthYearDisplay} · Contributor-wise daily incentive
                breakdown.
              </p>
            </div>
          </div>
          <div className="daily-ref-total-card">
            <span className="daily-ref-eyebrow">TOTAL EARNED</span>
            <strong>{formatEarningsCurrency(totalEarned)}</strong>
          </div>
        </header>

        <div className="daily-ref-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>
        </div>

        <DailyCommDetailsTable
          data={dailyCommissionDetails.data || []}
          loading={dailyCommissionDetails.loading}
        />

        <div className="daily-ref-mobile-list daily-ref-details-mobile-list">
          {(dailyCommissionDetails.data || []).map((item, index) => (
            <article key={index} className="daily-ref-detail-card">
              <div className="daily-ref-detail-top">
                <span className="daily-ref-mobile-index">{item.sNo}</span>
                <div className="daily-ref-detail-date">
                  <span>
                    <CalendarDays size={13} />
                    {item.date}
                  </span>
                  <strong>{item.contributors} Contributors</strong>
                </div>
                <strong className="daily-ref-detail-amount">
                  {formatEarningsCurrency(item.netAmount ?? item.receivedIncentives)}
                </strong>
                {item.tdsAmount != null && (
                  <small>
                    TDS {formatEarningsCurrency(item.tdsAmount)}
                    {item.tdsIsEstimated ? " (est)" : ""}
                  </small>
                )}
              </div>
            </article>
          ))}
        </div>

        {(!dailyCommissionDetails.data ||
          dailyCommissionDetails.data.length === 0) && (
          <div className="daily-ref-empty">
            <Sparkles size={20} /> No details found for this period.
          </div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={dailyCommissionDetails.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default DailyCommissoinDetails;
